import {
  getUser,
  type Connection,
  type HassUser,
} from "home-assistant-js-websocket";

export async function getCurrentUser(
  connection: Connection,
): Promise<HassUser> {
  return await getUser(connection);
}

/**
 * The OIDC claims `auth_oidc` captured for the signed-in user, as configured in
 * `claims.extra` (see homeassistant/README.md, "OIDC login").
 *
 * Both are null for a user who signed in with Home Assistant's own auth
 * provider — claims live on the OIDC credential, so those users have none.
 */
export interface UserClaims {
  /** Email as ScheduleMaster has it; what notifications will address. */
  email: string | null;
  /**
   * The subject exactly as the proxy issued it — the pilot's ScheduleMaster
   * userid. (HA's own identity for the user is this hashed with the issuer, so
   * it can't be used to identify them against ScheduleMaster.)
   */
  sub: string | null;
}

const NO_CLAIMS: UserClaims = { email: null, sub: null };

/**
 * Cached per connection: claims can't change without a new login, and a fresh
 * connection (reauth, reconnect) refetches. A failed lookup is dropped rather
 * than cached, so a later call retries.
 */
const cache = new WeakMap<Connection, Promise<UserClaims>>();

function claim(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

export function getUserClaims(connection: Connection): Promise<UserClaims> {
  const cached = cache.get(connection);
  if (cached) return cached;

  const pending = connection
    .sendMessagePromise<{ claims?: Record<string, unknown> }>({
      type: "auth_oidc/user_claims",
    })
    .then((result) => ({
      email: claim(result.claims?.email),
      sub: claim(result.claims?.sub),
    }))
    .catch(() => {
      // The command only exists when auth_oidc is loaded; without it (or if the
      // socket hiccups) callers carry on with no pilot identity attached.
      cache.delete(connection);
      return NO_CLAIMS;
    });

  cache.set(connection, pending);
  return pending;
}
